import { useState } from "react";
import { cn } from "@/lib/utils";
import { ImageThumbnail } from "../image-thumbnail/ImageThumbnail";
import { ImageStripLoadingSkeleton } from "./ImageStripLoadingSkeleton";
import { imageStripFrameClass } from "./imageStripLayout";

type ImageLoadPhase = "loading" | "ready" | "error";

function phaseFromImage(image: HTMLImageElement): ImageLoadPhase | null {
    if (!image.complete) {
        return null;
    }
    return image.naturalWidth > 0 ? "ready" : "error";
}

export function ImageStripImage({
    src,
    alt,
    isCover,
    width,
    height,
}: {
    src: string;
    alt: string;
    isCover: boolean;
    width?: number | null;
    height?: number | null;
}) {
    const [phase, setPhase] = useState<ImageLoadPhase>("loading");

    function syncImage(image: HTMLImageElement | null) {
        const next = image ? phaseFromImage(image) : null;
        if (next) {
            setPhase(next);
        }
    }

    if (phase === "error") {
        return (
            <ImageThumbnail
                alt={alt}
                className={imageStripFrameClass(isCover, false)}
            />
        );
    }

    const loading = phase === "loading";

    return (
        <div className={cn(loading && "relative w-fit")}>
            {loading ? (
                <ImageStripLoadingSkeleton
                    isCover={isCover}
                    width={width}
                    height={height}
                />
            ) : null}
            <ImageThumbnail
                imageRef={syncImage}
                src={src}
                alt={alt}
                className={
                    loading
                        ? "absolute inset-0 size-full opacity-0"
                        : imageStripFrameClass(isCover, true)
                }
                onLoad={(event) => {
                    const next = phaseFromImage(event.currentTarget);
                    if (next) {
                        setPhase(next);
                    }
                }}
                onError={() => {
                    setPhase("error");
                }}
            />
        </div>
    );
}
